<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import SrDataTable from '../tables/SrDataTable.vue'
import type { TableColumn } from '@/components/alerts/types'
import { ref, computed, watch, nextTick, markRaw } from 'vue'
import { ProfilesV2Service } from '@/services/profiles-v2-service'
import { PaymentsV2Service } from '@/services/payments-v2-service'
import type {
  UserProfileListResponse,
} from '@/services/profiles-v2-service/types'
import type { PaymentServiceAvailabilityResponse } from '@/services/payments-v2-service/types'
import { useMyToast } from '@/composable/useMyToast'
import { useTzDate } from '@/composable/useTzDate'
import { i18n } from '@/i18n'
import axios from 'axios'
// import PaymentModal from './PaymentModal.vue'
import { isNull } from 'lodash'
import { useThemeStore } from '@/stores/themeStore.ts'
import ProlongProfileButton from '../ProlongProfileButton.vue'

const { addToast } = useMyToast()
const { dateFullStr } = useTzDate()
const { t } = i18n.global

const visible = ref(false)
const loading = ref(false)
const data = ref<UserProfileListResponse | null>(null)
const services = ref<PaymentServiceAvailabilityResponse[]>([])
// const paymentModal = ref<InstanceType<typeof PaymentModal> | null>(null)
const prolongProfileNum = ref<number | undefined>(undefined)

const profiles = computed(() => data.value?.items ?? [])

// const buyProfileServices = computed(() =>
//   services.value
//     .filter((s) => s.service_type.startsWith('buy_profiles_') && s.can_purchase)
//     .sort((a, b) => a.service_type.localeCompare(b.service_type)),
// )

const toggle = () => {
  visible.value = !visible.value
}

defineExpose({
  toggle,
})

watch(visible, (v) => {
  if (v) {
    loadProfiles()
  } else {
    data.value = null
    services.value = []
    prolongProfileNum.value = undefined
  }
})

const loadProfiles = async () => {
  loading.value = true
  try {
    const [profilesRes, servicesRes] = await Promise.all([
      ProfilesV2Service.getProfiles(),
      PaymentsV2Service.getAvailableServices(),
    ])
    data.value = profilesRes.data
    services.value = servicesRes.data.items
  } catch (thrown) {
    handleApiError(thrown)
  } finally {
    loading.value = false
  }
}

const handleApiError = (thrown: unknown) => {
  if (axios.isAxiosError(thrown) && thrown.response) {
    const type = thrown.response.data?.detail?.[0]?.type
    if (type) {
      addToast({ severity: 'error', message: t(`modal.profiles.errors.${type}`) })
      return
    }
  }
  addToast({ severity: 'error', message: t('unexceptedError') })
}

const formatExpiresAt = (value: string | null) => {
  if (!value) return t('modal.profiles.permanent')
  return dateFullStr(value)
}

const statusLabel = (isActive: boolean) => {
  return isActive ? t('modal.profiles.active') : t('modal.profiles.expired')
}

const columns = computed<TableColumn[]>(() => [
  {
    field: 'num',
    header: 'modal.profiles.num',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'expires_at',
    header: 'modal.profiles.expiresAt',
    class: 'text-center w-50',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'is_active',
    header: 'modal.profiles.status',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as { is_active: boolean }
      return { class: `pit ${data.is_active ? 'success' : 'danger'}` }
    },
  },
  {
    field: 'actions',
    header: '',
    class: 'text-center',
    inner: markRaw(ProlongProfileButton),
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as { num: number; expires_at: string | null }
      const disabled = data.num <= 1 || isNull(data.expires_at)
      return {
        disabled: disabled,
        onClick: () => {
          if (disabled) return
          prolongProfile(data.num)
        },
      }
    },
  },
])

const formattingRules = computed(() => ({
  num: (v: number) => `#${v}`,
  expires_at: (v: string | null) => formatExpiresAt(v),
  is_active: (v: boolean) => statusLabel(v),
}))

// const buyProfile = (serviceType: string) => {
//   prolongProfileNum.value = undefined
//   paymentModal.value?.openWithPreset(serviceType)
// }

const themeStore = useThemeStore()
const paymentModal = computed(() => themeStore.paymentModal)

const prolongProfile = (num: number) => {
  prolongProfileNum.value = num
  nextTick(() => {
    paymentModal.value?.show(num)
  })
}
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:root="w-180" pt:content="p-0" pt:header="px-4 py-1"
    :header="$t('modal.profiles.title')" style="overflow: hidden">
    <!-- <div class="flex flex-wrap gap-2 justify-end mb-3 p-3">
      <Button v-for="service in buyProfileServices" :key="service.service_type" size="small" severity="success"
        variant="outlined" @click="buyProfile(service.service_type)">
        {{ $t('modal.profiles.buyProfile') }} {{ service.service_type.replace('buy_profiles_', '') }}
      </Button>
    </div> -->

    <SrDataTable size="small" :value="profiles" :columns="columns" :loading="loading"
      :formattingRules="formattingRules">
      <template #empty>
        <div class="text-center text-muted py-3">{{ $t('modal.profiles.empty') }}</div>
      </template>
    </SrDataTable>

    <!-- <PaymentModal ref="paymentModal" /> -->
  </AdaptiveDialog>
</template>
